// Support fix 2026-09-24: buyer-side Marketplace (shop) order history with live status timeline.
// Reads GET /api/marketplace/orders (buyer_id = current user). Refreshes every 15s.
import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { ShoppingBag } from 'lucide-react';
import api from '../utils/api';

const STEPS = [
  { key: 'pending', label: 'Placed' },
  { key: 'confirmed', label: 'Confirmed' },
  { key: 'packed', label: 'Packed' },
  { key: 'out_for_delivery', label: 'Out for delivery' },
  { key: 'delivered', label: 'Delivered' },
];
const LABELS = {
  pending: 'Placed', confirmed: 'Confirmed', preparing: 'Preparing', packed: 'Packed', shipped: 'Shipped',
  out_for_delivery: 'Out for delivery', delivered: 'Delivered', completed: 'Delivered', rejected: 'Rejected',
};
// Map any status to its position on the 5-step bar
const STEP_INDEX = { pending: 0, confirmed: 1, preparing: 1, packed: 2, shipped: 3, out_for_delivery: 3, delivered: 4, completed: 4 };

const fmt = (iso) => {
  try { return new Date(iso).toLocaleString('en-IN', { day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit' }); }
  catch (_) { return ''; }
};

export default function MarketplaceOrdersSection() {
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    const load = () => api.get('/marketplace/orders')
      .then((r) => { if (!cancelled) setOrders(r.data?.orders || []); })
      .catch(() => {})
      .finally(() => { if (!cancelled) setLoading(false); });
    load();
    const t = setInterval(load, 15000);
    return () => { cancelled = true; clearInterval(t); };
  }, []);

  if (loading) return <div className="glass-card p-6 text-center">Loading marketplace orders...</div>;

  if (orders.length === 0) {
    return (
      <div className="text-center py-16 glass-card" data-testid="no-marketplace-orders">
        <ShoppingBag className="w-16 h-16 mx-auto mb-4 text-muted-foreground" />
        <h2 className="text-2xl font-bold mb-2">No marketplace orders yet</h2>
        <Link to="/marketplace" className="text-primary underline">Browse the Marketplace</Link>
      </div>
    );
  }

  return (
    <div className="space-y-5" data-testid="marketplace-orders-list">
      {orders.map((o) => {
        const rejected = o.status === 'rejected';
        const idx = STEP_INDEX[o.status] ?? 0;
        const shops = [...new Set((o.items || []).map((i) => (i.shop_name || '').trim()).filter(Boolean))].join(', ');
        return (
          <div key={o.id} className="glass-card p-5" data-testid={`mk-order-${o.id}`}>
            <div className="flex justify-between items-start gap-3 mb-3">
              <div>
                <p className="text-lg font-bold">Order #{String(o.id).slice(0, 8)}</p>
                <p className="text-xs text-muted-foreground">{fmt(o.created_at)}{shops ? ` · ${shops}` : ''}</p>
              </div>
              <div className="text-right">
                <p className="text-xl font-bold text-primary">{Number(o.total_at || 0).toFixed(0)} AT</p>
                <span data-testid={`mk-order-status-${o.id}`} className={`text-xs px-3 py-1 rounded-full ${
                  rejected ? 'bg-red-500/20 text-red-400'
                    : (o.status === 'delivered' || o.status === 'completed') ? 'bg-emerald-500/20 text-emerald-400'
                    : 'bg-yellow-500/20 text-yellow-400'}`}>
                  {LABELS[o.status] || o.status}
                </span>
              </div>
            </div>

            <div className="border-t border-white/10 pt-3 space-y-1">
              {(o.items || []).map((it, i) => (
                <div key={i} className="flex justify-between text-sm">
                  <span className="text-muted-foreground">{it.title} × {it.quantity}</span>
                  <span className="font-semibold">{Number(it.line_total_at || 0).toFixed(0)} AT</span>
                </div>
              ))}
              {Number(o.delivery_at || 0) > 0 && (
                <div className="flex justify-between text-xs text-muted-foreground">
                  <span>Delivery</span><span>{Number(o.delivery_at).toFixed(0)} AT</span>
                </div>
              )}
            </div>

            {!rejected ? (
              <div className="mt-4 grid grid-cols-5 gap-1" data-testid={`mk-order-steps-${o.id}`}>
                {STEPS.map((s, i) => (
                  <div key={s.key} className="text-center">
                    <div className={`h-1.5 rounded-full ${i <= idx ? 'bg-emerald-400' : 'bg-white/10'}`} />
                    <div className={`text-[10px] mt-1 ${i <= idx ? 'text-emerald-300 font-bold' : 'text-muted-foreground'}`}>{s.label}</div>
                  </div>
                ))}
              </div>
            ) : (
              <div className="mt-4 p-2 rounded-lg bg-red-500/10 border border-red-500/20 text-xs text-red-300">
                Order rejected{o.refund_summary?.buyer_refunded_at ? ` · ${Number(o.refund_summary.buyer_refunded_at).toFixed(0)} AT refunded to your wallet` : ''}
              </div>
            )}

            {(o.tracking_updates || []).length > 0 && (
              <div className="mt-3 space-y-0.5">
                {[...o.tracking_updates].reverse().map((t, i) => (
                  <div key={i} className="text-[11px] text-muted-foreground flex gap-2">
                    <span className="shrink-0">{fmt(t.at)}</span>
                    <span>· {t.text || LABELS[t.status] || t.status}</span>
                  </div>
                ))}
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
}
